import doRequest from '../services/request';

/**
 * MV2-1609: o agendador de push voltou a morar no Central System (o plano original de
 * um microserviço apartado, `mk-ms-notification-push` atrás do gateway, foi revertido).
 * Este hook fala com o CS pelo mesmo caminho que `src/hooks/settings.ts` usa para os
 * demais endpoints de push (`push-notification`, `push-notification/preview`,
 * `push-notification/history`): `services/request.js` resolve `${apiUrl}/api/v1/...`
 * por tema/white-label e monta os headers (Api-Key, Platform, X-Timezone-Offset e
 * `Authorization: <token>` — SEM prefixo "Bearer", diferente do gateway).
 *
 * Por que `doRequest` (request.js) direto, e não os helpers de `services/documents.js`:
 * `documents.js`/`createDocumentRequest` NUNCA rejeita a promise — em erro HTTP ele só
 * resolve com `{ type: 'error', status, data }` (e só para uma lista fixa de status:
 * 400/401/403/404/410/429 — um 409, que é exatamente o "este agendamento já foi
 * processado" que a tela de edição/cancelamento trata, cai no ramo de SUCESSO). A UI
 * desta feature (PushScheduleTab, PushScheduleEditModal e o describeSaveError
 * compartilhado com PushNotificationConfigForm) já está pronta e não deve ser tocada
 * aqui — e ela espera o formato de erro do axios, `error.response.status`, para decidir
 * a mensagem (ver o comentário em PushNotificationConfigForm.jsx contrastando o envio
 * imediato, que "passa por services/request.js e sempre resolve", com o agendamento,
 * que "pode REJEITAR"). Usar `documents.js` aqui quebraria esse contrato em silêncio
 * (sucesso reportado numa falha de negócio) ou exigiria reescrever a UI — por isso a
 * camada abaixo chama `doRequest` (herdando headers e o logout automático em 401) e
 * normaliza a resposta ela mesma, rejeitando com a forma que a UI já espera.
 */
const SCHEDULES_PATH = 'push-notification/schedules';

export type RecurrenceKind = 'NONE' | 'WEEKLY' | 'MONTHLY';
export type ScheduleTargetType = 'TENANT' | 'USERS' | 'REGION';
export type ScheduleStatus =
  | 'SCHEDULED'
  | 'RUNNING'
  | 'DONE'
  | 'CANCELLED'
  | 'EXPIRED';

export interface ScheduleRegionFilter {
  regions?: string[];
  states?: string[];
  cities?: string[];
}

export interface PushScheduleInput {
  subject: string;
  message: string;
  targetType: ScheduleTargetType;
  userPks?: number[] | null;
  regionFilter?: ScheduleRegionFilter | null;
  /** ISO 8601 com offset, ex. 2026-09-05T08:00:00-03:00 */
  scheduledAt: string;
  timezone: string;
  recurrence: RecurrenceKind;
  /** YYYY-MM-DD; ignorado quando recurrence === 'NONE' */
  recurrenceUntil?: string | null;
}

export interface PushSchedule {
  uuid: string;
  subject: string;
  message: string;
  targetType: ScheduleTargetType;
  userPks?: number[] | null;
  regionFilter?: ScheduleRegionFilter | null;
  nextRunAt: string;
  timezone: string;
  recurrence: RecurrenceKind;
  recurrenceUntil?: string | null;
  status: ScheduleStatus;
  audienceEstimate?: number | null;
  createdByUserName?: string | null;
}

export interface PushScheduleQuery {
  page: number;
  size: number;
  status?: ScheduleStatus;
}

/** Exportada para teste: monta o caminho (relativo à API do CS) da listagem. */
export const buildScheduleUrl = (params: PushScheduleQuery): string => {
  const query = new URLSearchParams();
  query.set('page', String(params.page));
  query.set('size', String(params.size));
  if (params.status) {
    query.set('status', params.status);
  }
  return `${SCHEDULES_PATH}?${query.toString()}`;
};

/**
 * Exportada para teste: normaliza o corpo antes do POST de criação.
 * O backend guarda o FILTRO, nunca a audiência resolvida — por isso só o campo
 * do alvo escolhido é enviado, e os demais saem do payload.
 */
export const toSchedulePayload = (input: PushScheduleInput) => {
  const payload: Record<string, unknown> = {
    subject: input.subject,
    message: input.message,
    targetType: input.targetType,
    scheduledAt: input.scheduledAt,
    timezone: input.timezone,
    recurrence: input.recurrence,
  };
  if (input.targetType === 'USERS' && input.userPks && input.userPks.length > 0) {
    payload.userPks = input.userPks;
  }
  if (input.targetType === 'REGION' && input.regionFilter) {
    payload.regionFilter = input.regionFilter;
  }
  if (input.recurrence !== 'NONE' && input.recurrenceUntil) {
    payload.recurrenceUntil = input.recurrenceUntil;
  }
  return payload;
};

/** Campos que a tela de edição altera. A audiência não está aqui de propósito. */
export interface PushSchedulePatch {
  subject: string;
  message: string;
  scheduledAt: string;
  timezone: string;
  recurrence: RecurrenceKind;
  recurrenceUntil?: string | null;
}

/**
 * Exportada para teste: corpo do PATCH de edição. Só os campos editáveis — nem
 * targetType, nem userPks, nem regionFilter.
 *
 * `recurrenceUntil` vai explicitamente como null quando a recorrência é desligada:
 * num PATCH, omitir o campo significa "não mexe", e a data-fim antiga ficaria no
 * servidor apontando para uma série que não existe mais.
 */
export const toSchedulePatch = (input: PushSchedulePatch) => ({
  subject: input.subject,
  message: input.message,
  scheduledAt: input.scheduledAt,
  timezone: input.timezone,
  recurrence: input.recurrence,
  recurrenceUntil:
    input.recurrence === 'NONE' ? null : input.recurrenceUntil ?? null,
});

const parseJsonBody = async (raw: any): Promise<any> => {
  if (!raw || typeof raw.json !== 'function') {
    return undefined;
  }
  try {
    return await raw.json();
  } catch {
    // corpo vazio (ex.: 204 do cancel) ou não-JSON — não é uma falha de parsing que
    // importe para quem chama.
    return undefined;
  }
};

/**
 * Chama `doRequest` (services/request.js) — reaproveitando headers e o logout
 * automático em 401 — e rejeita explicitamente quando a resposta não é de sucesso,
 * no formato `{ response: { status, data } }` que `scheduleErrors.ts` e a UI desta
 * feature já esperam (ver o comentário no topo do arquivo).
 */
const callSchedulesApi = async (
  url: string,
  options: { method: string; body?: string },
): Promise<any> => {
  const raw: any = await doRequest(url, options);
  const status = raw?.status;
  const data = await parseJsonBody(raw);
  const ok = typeof raw?.ok === 'boolean' ? raw.ok : status >= 200 && status < 300;

  if (!ok) {
    const error: any = new Error(
      `Falha na requisição de agendamento de push (status ${status ?? 'desconhecido'})`,
    );
    error.response = { status, data };
    throw error;
  }

  return data;
};

export const getPushSchedules = async (params: PushScheduleQuery) => {
  const data = await callSchedulesApi(buildScheduleUrl(params), { method: 'GET' });
  return data as { results: PushSchedule[]; count: number };
};

export const createPushSchedule = async (input: PushScheduleInput) => {
  const data = await callSchedulesApi(SCHEDULES_PATH, {
    method: 'POST',
    body: JSON.stringify(toSchedulePayload(input)),
  });
  return data as PushSchedule;
};

/**
 * A edição é um PATCH, não um PUT.
 *
 * A tela de edição altera título, mensagem, horário e recorrência — nunca a audiência.
 * Montar um PUT completo a partir da linha da listagem obrigaria a apostar que o
 * GET /schedules devolve `userPks` inteiro (podem ser centenas de PKs por linha), e o
 * contrato da listagem ainda não existe: se vier um resumo, editar o título de uma
 * campanha "Por Usuário" de 400 destinatários mandaria um PUT sem `userPks` e a
 * campanha perderia a audiência (ou voltaria 400). O PATCH com só os campos editáveis
 * é o que a tela realmente faz e não depende de contrato nenhum.
 */
export const updatePushSchedule = async (uuid: string, patch: PushSchedulePatch) => {
  const data = await callSchedulesApi(`${SCHEDULES_PATH}/${uuid}`, {
    method: 'PATCH',
    body: JSON.stringify(toSchedulePatch(patch)),
  });
  return data as PushSchedule;
};

export const cancelPushSchedule = async (uuid: string) => {
  await callSchedulesApi(`${SCHEDULES_PATH}/${uuid}/cancel`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
};
